import axios from 'axios';

/**
 * Servicio del catálogo de módulos.
 *
 * La fuente de verdad de los módulos vive en el proyecto `flow`
 * (src/modules/config/modules.meta.ts), expuesta vía GET {flow}/api/modules.
 *
 * Para evitar el bloqueo CORS del navegador en producción, NO se llama a flow
 * directo: se llama al backend de tadmin (GET /api/modules), que actúa de proxy
 * server-to-server hacia flow (ver backend/routes/modules.js). En el backend se
 * configura FLOW_URL con el dominio de flow.
 *
 * Este servicio construye la sección "Módulos Requeridos" de PlanesForm sin
 * mantener una lista fija.
 */

// Resolución de la URL del backend (mismo patrón que virtualPosService):
// en prod usa rutas relativas (mismo origen); en dev local, http://localhost:3010.
const getApiBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl && !backendUrl.includes('localhost')) {
    return backendUrl.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return '';
  }
  return 'http://localhost:3010';
};

const API_BASE_URL = getApiBaseUrl();

// Fallback usado solo si flow no responde, para no dejar la sección vacía.
// Refleja los módulos conocidos de MODULES_CONFIG (puede quedar desactualizado;
// el camino normal es el fetch al endpoint).
const FALLBACK_MODULOS = [
  { id: 'dps', label: 'Declaración Personal de Salud' },
  { id: 'dps_simple', label: 'Enfermedades No Cubiertas' },
  { id: 'fotos', label: 'Subir Fotos' },
  { id: 'declaraciones', label: 'Declaraciones Adicionales' },
  { id: 'herederos', label: 'Designación de Herederos' },
  { id: 'cuenta_reembolso', label: 'Cuenta para Reembolsos' },
  { id: 'datos_incendio', label: 'Datos de la Propiedad' },
  { id: 'direccion_riesgo', label: 'Dirección del Riesgo' },
  { id: 'declaracion_demo', label: 'Condiciones de la Propiedad' },
];

class ModulosService {
  constructor() {
    this.cache = null;
    this.cacheExpiry = 0;
    this.cacheTime = 600000; // 10 minutos
  }

  /**
   * Devuelve los módulos disponibles como [{ id, label, description }].
   * Cachea en memoria y, ante error de red, devuelve el fallback.
   */
  async getModulosDisponibles() {
    if (this.cache && Date.now() < this.cacheExpiry) {
      return this.cache;
    }

    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/modules`, { timeout: 8000 });
      const modulos = (Array.isArray(data) ? data : [])
        .filter((m) => m && m.id)
        .map((m) => ({ id: m.id, label: m.name || m.id, description: m.description || '' }));

      if (modulos.length > 0) {
        this.cache = modulos;
        this.cacheExpiry = Date.now() + this.cacheTime;
        return modulos;
      }
      return FALLBACK_MODULOS;
    } catch (error) {
      console.warn('[modulosService] no se pudo leer /api/modules (proxy backend → flow), usando fallback:', error.message);
      return FALLBACK_MODULOS;
    }
  }
}

const modulosService = new ModulosService();
export default modulosService;
